import React, { useEffect, useRef } from 'react';
import { MapPin, Clock, AlertTriangle } from 'lucide-react';

// Inject CSS animation for auto-scrolling faults
const injectScrollAnimation = () => {
  if (document.getElementById('fault-scroll-animation')) return;
  const style = document.createElement('style');
  style.id = 'fault-scroll-animation';
  style.textContent = `
    @keyframes scrollFaults {
      0%, 10% { transform: translateY(0); }
      90%, 100% { transform: translateY(-50%); }
    }
  `;
  document.head.appendChild(style);
};

const statusColors = {
  active: { bg: '#10b981', border: '#10b981', text: 'Aktiivinen' },
  maintenance: { bg: '#f59e0b', border: '#f59e0b', text: 'Huollossa' },
  retired: { bg: '#6b7280', border: '#6b7280', text: 'Poistettu' },
  inactive: { bg: '#6b7280', border: '#6b7280', text: 'Ei käytössä' }
};

const severityColors = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#94a3b8'
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just nyt';
  if (diffMins < 60) return `${diffMins} min sitten`;
  if (diffHours < 24) return `${diffHours}h sitten`;
  if (diffDays === 1) return 'Eilen';
  return `${diffDays} pv sitten`;
};

const AssetCard = ({ device, fontScale = 'medium', showFaults = true, compact = false, customScale = 1.0, customImageHeight = null }) => {
  const status = statusColors[device.status] || statusColors.inactive;
  const timeAgo = formatTimeAgo(device.location_updated_at);
  const recentFaults = device.recent_faults || [];
  const faultsRef = useRef(null);

  // Inject animation on mount
  useEffect(() => {
    injectScrollAnimation();
  }, []);

  // Perusfontit (vw-yksiköissä 4K:lle)
  const baseSizes = {
    name: 1.3,      // vw
    status: 0.9,    // vw
    location: 0.8,  // vw
    badge: 0.9,     // vw
    badgeSize: 2.2, // vw
    fault: 0.75,    // vw
    padding: 0.6,   // vw
    gap: 0.4,       // vw
    dotSize: 0.4,   // vw
    iconSize: 28,   // px
  };

  // Skaalaa kaikki fontit customScale-kertoimella
  const scale = customScale;
  const sizes = {
    name: `${baseSizes.name * scale}vw`,
    status: `${baseSizes.status * scale}vw`,
    location: `${baseSizes.location * scale}vw`,
    badge: `${baseSizes.badge * scale}vw`,
    badgeSize: `${baseSizes.badgeSize * scale}vw`,
    fault: `${baseSizes.fault * scale}vw`,
    padding: `${baseSizes.padding * scale}vw`,
    gap: `${baseSizes.gap * scale}vw`,
    dotSize: `${baseSizes.dotSize * scale}vw`,
    iconSize: Math.round(baseSizes.iconSize * scale),
    imageHeight: customImageHeight ? `${customImageHeight}%` : '30%',
  };

  // Compact mobile layout
  if (compact) {
    return (
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        border: `2px solid ${status.border}40`,
        borderLeft: `4px solid ${status.border}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        boxShadow: `0 0 8px ${status.border}20`
      }}>
        {/* Image area - left side */}
        <div style={{
          backgroundColor: '#0f172a',
          width: '100px',
          minWidth: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {device.image_url ? (
            <img
              src={device.image_url}
              alt={device.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="color: #475569; font-size: 2.5rem;">🏭</div>';
              }}
            />
          ) : (
            <div style={{ color: '#475569', fontSize: '2.5rem' }}>🏭</div>
          )}

          {/* Fault badge */}
          {device.active_faults > 0 && (
            <div style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              backgroundColor: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {device.active_faults}
            </div>
          )}
        </div>

        {/* Info area - right side */}
        <div style={{
          padding: '0.75rem',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
          overflow: 'hidden'
        }}>
          {/* Name */}
          <div style={{
            fontWeight: 'bold',
            fontSize: '1rem',
            color: '#f1f5f9',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {device.name}
          </div>

          {/* Status & faults row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <span style={{
              backgroundColor: status.bg,
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: '500'
            }}>
              {status.text}
            </span>

            {device.active_faults > 0 && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: '#ef4444',
                fontSize: '0.8rem'
              }}>
                <AlertTriangle size={12} />
                {device.active_faults} vikaa
              </span>
            )}
          </div>

          {/* Recent fault preview (just one on mobile) */}
          {showFaults && recentFaults.length > 0 && (
            <div style={{
              backgroundColor: '#0f172a',
              borderRadius: '4px',
              padding: '0.4rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.3rem'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: severityColors[recentFaults[0].severity] || severityColors.medium,
                flexShrink: 0,
                marginTop: '0.3rem'
              }} />
              <span style={{
                fontSize: '0.75rem',
                color: '#cbd5e1',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {recentFaults[0].description}
              </span>
            </div>
          )}

          {/* Location & time */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginTop: 'auto',
            flexWrap: 'wrap'
          }}>
            {device.location && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: '#94a3b8',
                fontSize: '0.75rem'
              }}>
                <MapPin size={12} />
                <span style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '120px'
                }}>
                  {device.location}
                </span>
              </div>
            )}

            {timeAgo && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: '#64748b',
                fontSize: '0.7rem'
              }}>
                <Clock size={10} />
                {timeAgo}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout - text focused, no image
  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: '12px',
      border: `2px solid ${status.border}40`,
      borderLeft: `4px solid ${status.border}`,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      boxShadow: `0 0 8px ${status.border}20`
    }}>
      {/* Fault count badge - top right corner */}
      {device.active_faults > 0 && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          backgroundColor: '#ef4444',
          color: 'white',
          borderRadius: '50%',
          width: '1.5rem',
          height: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          {device.active_faults}
        </div>
      )}

      {/* Header - fixed position */}
      <div style={{
        padding: sizes.padding,
        paddingBottom: '0',
        flexShrink: 0
      }}>
        {/* Name with status dot */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{
            fontWeight: 'bold',
            fontSize: sizes.name,
            color: '#f1f5f9',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {device.name}
          </span>
          {/* Status dot */}
          <span style={{
            width: sizes.dotSize,
            height: sizes.dotSize,
            borderRadius: '50%',
            backgroundColor: status.bg,
            flexShrink: 0,
            boxShadow: `0 0 6px ${status.bg}`
          }} />
        </div>
      </div>

      {/* Faults area - maximized with auto-scroll */}
      <div style={{
        flex: 1,
        margin: sizes.padding,
        marginTop: sizes.gap,
        marginBottom: '0',
        backgroundColor: '#0f172a',
        borderRadius: '6px',
        padding: sizes.padding,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {recentFaults.length > 0 ? (
          <div
            className="auto-scroll-faults"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: sizes.gap,
              animation: recentFaults.length > 3 ? 'scrollFaults 20s linear infinite' : 'none'
            }}
          >
            {recentFaults.map((fault, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem'
                }}
              >
                <span style={{
                  width: sizes.dotSize,
                  height: sizes.dotSize,
                  borderRadius: '50%',
                  backgroundColor: severityColors[fault.severity] || severityColors.medium,
                  flexShrink: 0,
                  marginTop: '0.3em'
                }} />
                <span style={{
                  fontSize: sizes.fault,
                  color: '#cbd5e1',
                  lineHeight: 1.4
                }}>
                  {fault.description}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            color: '#475569',
            fontSize: sizes.fault,
            fontStyle: 'italic'
          }}>
            Ei vikailmoituksia
          </div>
        )}
      </div>

      {/* Footer - fixed position at bottom */}
      <div style={{
        padding: sizes.padding,
        paddingTop: sizes.gap,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#64748b',
        fontSize: sizes.location
      }}>
        {/* Location - always show */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem'
        }}>
          <MapPin size={sizes.iconSize} />
          <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontStyle: device.location ? 'normal' : 'italic',
            color: device.location ? '#94a3b8' : '#475569'
          }}>
            {device.location || 'Ei sijaintia'}
          </span>
        </div>

        {/* Time ago */}
        {timeAgo && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem'
          }}>
            <Clock size={sizes.iconSize} />
            {timeAgo}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetCard;
